import { useTranslation } from "react-i18next";
import Navbar from "../components/Navbar";
import HelpCenter from "../components/HelpCenter";

const FAQ_GROUPS = [
  {
    id: "gettingStarted",
    titleKey: "adminFaq.groups.gettingStarted.title",
    labelKey: "adminFaq.groups.gettingStarted.label",
    items: [
      {
        id: "setup",
        questionKey: "adminFaq.groups.gettingStarted.items.setup.question",
        answerKey: "adminFaq.groups.gettingStarted.items.setup.answer",
      },
      {
        id: "publicPage",
        questionKey: "adminFaq.groups.gettingStarted.items.publicPage.question",
        answerKey: "adminFaq.groups.gettingStarted.items.publicPage.answer",
      },
      {
        id: "trial",
        questionKey: "adminFaq.groups.gettingStarted.items.trial.question",
        answerKey: "adminFaq.groups.gettingStarted.items.trial.answer",
      },
    ],
  },
  {
    id: "songRequests",
    titleKey: "adminFaq.groups.songRequests.title",
    labelKey: "adminFaq.groups.songRequests.label",
    items: [
      {
        id: "sendRequest",
        questionKey: "adminFaq.groups.songRequests.items.sendRequest.question",
        answerKey: "adminFaq.groups.songRequests.items.sendRequest.answer",
      },
      {
        id: "closeRequests",
        questionKey: "adminFaq.groups.songRequests.items.closeRequests.question",
        answerKey: "adminFaq.groups.songRequests.items.closeRequests.answer",
      },
      {
        id: "hideSongs",
        questionKey: "adminFaq.groups.songRequests.items.hideSongs.question",
        answerKey: "adminFaq.groups.songRequests.items.hideSongs.answer",
      },
    ],
  },
  {
    id: "songLibrary",
    titleKey: "adminFaq.groups.songLibrary.title",
    labelKey: "adminFaq.groups.songLibrary.label",
    items: [
      {
        id: "addSongs",
        questionKey: "adminFaq.groups.songLibrary.items.addSongs.question",
        answerKey: "adminFaq.groups.songLibrary.items.addSongs.answer",
      },
      {
        id: "suggestions",
        questionKey: "adminFaq.groups.songLibrary.items.suggestions.question",
        answerKey: "adminFaq.groups.songLibrary.items.suggestions.answer",
      },
      {
        id: "songLimit",
        questionKey: "adminFaq.groups.songLibrary.items.songLimit.question",
        answerKey: "adminFaq.groups.songLibrary.items.songLimit.answer",
      },
    ],
  },
  {
    id: "plusFeatures",
    titleKey: "adminFaq.groups.plusFeatures.title",
    labelKey: "adminFaq.groups.plusFeatures.label",
    items: [
      {
        id: "included",
        questionKey: "adminFaq.groups.plusFeatures.items.included.question",
        answerKey: "adminFaq.groups.plusFeatures.items.included.answer",
      },
      {
        id: "reader",
        questionKey: "adminFaq.groups.plusFeatures.items.reader.question",
        answerKey: "adminFaq.groups.plusFeatures.items.reader.answer",
      },
      {
        id: "gigNotes",
        questionKey: "adminFaq.groups.plusFeatures.items.gigNotes.question",
        answerKey: "adminFaq.groups.plusFeatures.items.gigNotes.answer",
      },
    ],
  },
];

const faqCount = FAQ_GROUPS.reduce(
  (total, group) => total + group.items.length,
  0
);

export default function AdminFaqPage({ config }) {
  const { t } = useTranslation("admin");
  function handleLogout() {
    window.sessionStorage.removeItem("paranoidi_logged_in");
    window.sessionStorage.removeItem("paranoidi_auth_performer");
    window.sessionStorage.removeItem("paranoidi_auth_user");
    window.location.href = "/";
  }

  return (
    <main
      className={[
        "min-h-screen overflow-hidden bg-black text-white",
        "transition-colors duration-300",
        "theme-light:bg-[#ead6c2] theme-light:text-[#241a16]",
      ].join(" ")}
    >
      <Navbar config={config} onLogout={handleLogout} />

      <section className="relative px-4 pb-12 pt-28 sm:px-6 sm:pt-32 lg:px-8">
        {/* Hidas Paranoidi-hehku sivun taustalla. */}
        <div
          className={[
            "pointer-events-none absolute inset-0",
            "bg-[radial-gradient(circle_at_50%_0%,rgba(239,68,68,0.24),transparent_34rem)]",
            "theme-light:bg-[radial-gradient(circle_at_50%_0%,rgba(239,68,68,0.14),transparent_34rem)]",
          ].join(" ")}
        />

        <div
          className={[
            "pointer-events-none absolute left-1/2 top-16 h-72 w-72",
            "-translate-x-1/2 rounded-full bg-red-500/10 blur-3xl",
            "motion-safe:animate-[pulse_7s_ease-in-out_infinite]",
            "theme-light:bg-red-500/[0.07]",
          ].join(" ")}
        />

        <div
          className={[
            "pointer-events-none absolute inset-0 opacity-[0.055]",
            "[background-image:linear-gradient(rgba(255,255,255,.8)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.8)_1px,transparent_1px)]",
            "[background-size:42px_42px]",
            "theme-light:opacity-[0.025]",
          ].join(" ")}
        />

        <div className="relative mx-auto max-w-6xl">
          <header
            className={[
              "relative overflow-hidden rounded-[2rem] border border-white/10",
              "bg-white/[0.035] p-6 shadow-2xl shadow-black/40 sm:p-8 lg:p-10",
              "motion-safe:animate-[fadeIn_500ms_ease-out_both]",
              "theme-light:border-[#5a3824]/12 theme-light:bg-[#fff8ef]",
              "theme-light:shadow-[0_24px_65px_rgba(58,43,24,0.12)]",
            ].join(" ")}
          >
            <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-red-500/15 blur-3xl theme-light:bg-red-500/10" />

            <div className="relative grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-xs font-black uppercase tracking-[0.24em] text-red-400 theme-light:text-[#b42318]">
                    {t("adminFaq.header.eyebrow")}
                  </p>

                  <span
                    className={[
                      "rounded-full border border-red-400/25 bg-red-500/10",
                      "px-3 py-1 text-[0.62rem] font-black uppercase",
                      "tracking-[0.16em] text-red-200",
                      "theme-light:border-red-500/25 theme-light:bg-red-50",
                      "theme-light:text-[#b42318]",
                    ].join(" ")}
                  >
                    {t("adminFaq.header.signalBadge")}
                  </span>
                </div>

                <h1
                  className={[
                    "mt-4 max-w-4xl text-4xl font-black uppercase",
                    "leading-[0.92] tracking-[-0.065em] text-white sm:text-6xl",
                    "theme-light:text-[#241a16]",
                  ].join(" ")}
                >
                  {t("adminFaq.header.title")}
                </h1>

                <p className="mt-5 max-w-3xl text-base font-semibold leading-8 text-zinc-400 theme-light:text-[#5a3824]">
                  {t("adminFaq.header.description")}
                </p>
              </div>

              <div
                className={[
                  "relative min-w-[12rem] overflow-hidden rounded-[1.5rem]",
                  "border border-red-400/20 bg-red-500/10 p-5",
                  "theme-light:border-red-500/20",
                  "theme-light:bg-[linear-gradient(135deg,#f2d8d5,#fff8ef)]",
                ].join(" ")}
              >
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-red-400 to-transparent" />

                <p className="text-[0.62rem] font-black uppercase tracking-[0.2em] text-red-300 theme-light:text-[#b42318]">
                  {t("adminFaq.header.bankLabel")}
                </p>

                <div className="mt-3 flex items-end gap-3">
                  <span className="text-5xl font-black tracking-[-0.08em] text-white theme-light:text-[#541717]">
                    {faqCount}
                  </span>

                  <span className="pb-1 text-xs font-black uppercase tracking-[0.14em] text-zinc-400 theme-light:text-[#8a5a35]">
                    {t("adminFaq.header.answersLabel")}
                  </span>
                </div>

                <div className="mt-4 flex h-5 items-end gap-1">
                  {[8, 13, 18, 11, 16, 7, 14, 20, 10].map((height, index) => (
                    <span
                      key={`${height}-${index}`}
                      className={[
                        "w-1.5 rounded-full bg-red-400/70",
                        "motion-safe:animate-[pulse_2.8s_ease-in-out_infinite]",
                      ].join(" ")}
                      style={{
                        height,
                        animationDelay: `${index * 120}ms`,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </header>

          <div className="mt-8 grid gap-5 lg:grid-cols-2">
            {FAQ_GROUPS.map((group, groupIndex) => (
              <section
                key={group.id}
                className={[
                  "group/card relative overflow-hidden rounded-[2rem]",
                  "border border-white/10 bg-zinc-950/80 p-5",
                  "shadow-2xl shadow-black/30 transition duration-300",
                  "hover:-translate-y-1 hover:border-red-400/25",
                  "motion-safe:animate-[fadeIn_500ms_ease-out_both]",
                  "theme-light:border-[#5a3824]/12 theme-light:bg-[#fff8ef]",
                  "theme-light:shadow-[0_18px_48px_rgba(58,43,24,0.10)]",
                  "theme-light:hover:border-red-500/25",
                ].join(" ")}
                style={{ animationDelay: `${100 + groupIndex * 90}ms` }}
              >
                <div className="pointer-events-none absolute right-0 top-0 h-28 w-28 rounded-bl-full bg-red-500/[0.06] transition group-hover/card:bg-red-500/10" />

                <div className="relative flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[0.6rem] font-black uppercase tracking-[0.22em] text-red-400 theme-light:text-[#b42318]">
                      {t(group.labelKey)}
                    </p>

                    <h2 className="mt-2 text-2xl font-black uppercase tracking-[-0.04em] text-white theme-light:text-[#241a16]">
                      {t(group.titleKey)}
                    </h2>
                  </div>

                  <span
                    className={[
                      "flex h-11 w-11 shrink-0 items-center justify-center",
                      "rounded-full border border-red-400/25 bg-red-500/10",
                      "text-xs font-black text-red-200",
                      "theme-light:border-red-500/25 theme-light:bg-red-50",
                      "theme-light:text-[#b42318]",
                    ].join(" ")}
                  >
                    {String(groupIndex + 1).padStart(2, "0")}
                  </span>
                </div>

                <div className="relative mt-5 grid gap-3">
                  {group.items.map((item, itemIndex) => (
                    <details
                      key={item.id}
                      className={[
                        "group overflow-hidden rounded-2xl border border-white/10",
                        "bg-black/45 transition duration-300",
                        "hover:border-red-400/30 hover:bg-red-500/[0.04]",
                        "open:border-red-400/35 open:bg-red-500/[0.06]",
                        "theme-light:border-[#5a3824]/12 theme-light:bg-white/65",
                        "theme-light:hover:border-red-500/30",
                        "theme-light:hover:bg-[#fffdf9]",
                        "theme-light:open:border-red-500/35",
                        "theme-light:open:bg-[#fdf1eb]",
                      ].join(" ")}
                    >
                      <summary
                        className={[
                          "cursor-pointer list-none px-4 py-4",
                          "text-sm font-black uppercase leading-6",
                          "tracking-[0.07em] text-zinc-100",
                          "theme-light:text-[#541717]",
                        ].join(" ")}
                      >
                        <span className="flex w-full items-center justify-between gap-4">
                          <span className="flex min-w-0 items-start gap-3">
                            <span className="mt-0.5 text-[0.58rem] text-red-400">
                              {String(itemIndex + 1).padStart(2, "0")}
                            </span>

                            <span>{t(item.questionKey)}</span>
                          </span>

                          <span
                            className={[
                              "flex h-7 w-7 shrink-0 items-center justify-center",
                              "rounded-full border border-red-400/30",
                              "text-sm text-red-200 transition duration-300",
                              "group-open:rotate-45 group-open:bg-red-500",
                              "group-open:text-white",
                              "theme-light:border-red-500/30",
                              "theme-light:bg-red-50 theme-light:text-[#b42318]",
                            ].join(" ")}
                          >
                            +
                          </span>
                        </span>
                      </summary>

                      {/* Grid-rivillä vastaus avautuu pehmeästi ilman JS-tilaa. */}
                      <div
                        className={[
                          "grid grid-rows-[0fr] opacity-0",
                          "transition-[grid-template-rows,opacity] duration-300",
                          "group-open:grid-rows-[1fr] group-open:opacity-100",
                        ].join(" ")}
                      >
                        <div className="overflow-hidden">
                          <p
                            className={[
                              "mx-4 border-t border-white/10 pb-5 pt-4",
                              "text-sm font-semibold leading-7 text-zinc-400",
                              "theme-light:border-[#5a3824]/10",
                              "theme-light:text-[#5a3824]",
                            ].join(" ")}
                          >
                            {t(item.answerKey)}
                          </p>
                        </div>
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div
            className={[
              "relative mt-8 overflow-hidden rounded-[2rem]",
              "border border-red-400/20 bg-red-500/10 p-6 text-center",
              "shadow-2xl shadow-black/30 sm:p-8",
              "motion-safe:animate-[fadeIn_500ms_ease-out_450ms_both]",
              "theme-light:border-red-500/25",
              "theme-light:bg-[linear-gradient(135deg,#f2d8d5,#fff8ef)]",
              "theme-light:shadow-[0_20px_55px_rgba(84,23,23,0.12)]",
            ].join(" ")}
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-red-400 to-transparent" />

            <p className="text-[0.62rem] font-black uppercase tracking-[0.22em] text-red-300 theme-light:text-[#b42318]">
              {t("adminFaq.footer.eyebrow")}
            </p>

            <h2 className="mt-2 text-2xl font-black uppercase tracking-[-0.04em] text-white theme-light:text-[#541717]">
              {t("adminFaq.footer.title")}
            </h2>

            <p className="mx-auto mt-3 max-w-2xl text-sm font-semibold leading-7 text-red-50/80 theme-light:text-[#5a3824]">
              {t("adminFaq.footer.text")}
            </p>
          </div>
        </div>
      </section>

      <HelpCenter />
    </main>
  );
}